import { clsx } from 'clsx';
import type { ReactNode } from 'react';
import styles from './ui.module.css';

/**
 * Поверхность карточки: контур со свечением на бумаге Мезы.
 *
 * Заменяет собой <Group mode="plain"> вокруг каждой строки списка. Так было нельзя:
 * VKUI отдаёт группе mode="plain" при ширине меньше планшета, то есть в
 * мини-аппе на телефоне ВСЕГДА, и от карточки не остаётся ни рамки, ни
 * скруглений, ни внешних отступов — список сливается в сплошную ленту.
 * <Group mode="plain"> остаётся секцией, а не строкой.
 */

/**
 * Состояние вещи, которое видно по контуру раньше, чем по бейджу.
 *
 * `published` — то, что живо и стоит в реестре: бирюзовый контур. В макетах
 * это самый частый контур карточки, и он же единственный, который что-то
 * СООБЩАЕТ: фиолетовый у нейтральной карточки, сиреневый у того, что ждёт
 * решения, малиновый у отказа.
 */
export type CardTone = 'default' | 'published' | 'pending' | 'rejected' | 'archived';

const TONE: Record<CardTone, string> = {
  default: '',
  published: styles.cardPublished,
  pending: styles.cardPending,
  rejected: styles.cardRejected,
  archived: styles.cardArchived,
};

interface CardProps {
  children: ReactNode;
  tone?: CardTone;
  /**
   * Отступы внутри. Снимают там, где карточка составная: миниатюра во всю
   * ширину, полоса действий по нижнему краю — им своя плотность.
   */
  padded?: boolean;
  /**
   * Отметка в ПРАВОМ ВЕРХНЕМ углу карточки — там, где у карточки нет строки
   * заголовка, к которой знак можно было бы пристроить. У профиля это право
   * записи: оно про весь аккаунт, а не про строку «Роль», и строкой снизу
   * читалось как третье значение перечня.
   *
   * Знак стоит В ПОТОКЕ, сбоку от содержимого, а не поверх него: содержимое
   * занимает остаток ширины и под знак не подъезжает. Своей строкой он тоже
   * не встаёт — перечень «ярлык — значение» должен остаться на своём месте,
   * у верхнего края карточки.
   */
  corner?: ReactNode;
}

export function Card({ children, tone = 'default', padded = true, corner }: CardProps) {
  const body = padded ? <div className={styles.cardBody}>{children}</div> : children;

  return (
    <div className={clsx(styles.card, TONE[tone])}>
      {corner === undefined ? (
        body
      ) : (
        <div className={styles.cardCorner}>
          {body}
          <span className={styles.cardCornerMark}>{corner}</span>
        </div>
      )}
    </div>
  );
}

/**
 * Полоса поверх карточки: значок и одно слово о её состоянии.
 *
 * Ставится в `Card padded={false}` перед телом. Нужна там, где состояние —
 * это всё содержание карточки: «доступа нет», «запись закрыта». Значком в
 * строке заголовка то же самое читается как примечание к тексту, а не как
 * заголовок самой карточки.
 */
export function CardBanner({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <div className={styles.cardBanner}>
      <span className={styles.cardBannerScan} />
      <span className={styles.cardBannerIcon}>{icon}</span>
      <span className={styles.cardBannerText}>{children}</span>
    </div>
  );
}

/** Тело карточки, у которой есть полоса: отступы те же, что у padded. */
export function CardBody({ children }: { children: ReactNode }) {
  return <div className={styles.cardBody}>{children}</div>;
}
